// src/pages/Teachers.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  PlusIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BookOpenIcon,
  UserGroupIcon,
  AcademicCapIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  PrinterIcon,
  DocumentArrowDownIcon,
  DocumentIcon,
  Squares2X2Icon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import apiClient from "../api/client";
import { FireLoader } from "../components/Loader";
import { useTeacherSocket } from "../hooks/useTeacherSocket";
import { useAuth } from "../context/AuthContext";
import {
  showConfirm,
  showSuccess,
  showError,
  showWarning,
} from "../utils/alert";

export default function Teachers() {
  const { planAccess } = useAuth();
  const isStarterPlan =
    String(planAccess?.plan?.name || "").trim().toLowerCase() === "starter";
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTeacherStatus, setFilterTeacherStatus] = useState("all");
  const [filterGender, setFilterGender] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showMenu, setShowMenu] = useState(null);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isExportingWord, setIsExportingWord] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [itemsPerPage] = useState(10);
  const [hoveredGender, setHoveredGender] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("teachersViewMode") || "grid";
    } catch {
      return "grid";
    }
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    onLeave: 0,
    senior: 0,
    male: 0,
    female: 0,
  });

  const fetchTeachersRef = useRef();
  const actionMenuRef = useRef(null);

  // ─── Fetch teachers with optional background mode ───
  const fetchTeachers = async (options = { background: false }) => {
    try {
      if (!options.background) {
        setLoading(true);
      }

      const allResponse = await apiClient.get("/teachers", {
        params: {
          page: 1,
          limit: 1000,
        },
      });

      let allData = [];
      let totalCount = 0;
      let activeCount = 0;
      let onLeaveCount = 0;
      let seniorCount = 0;
      let maleCount = 0;
      let femaleCount = 0;

      if (allResponse.data) {
        if (Array.isArray(allResponse.data)) {
          allData = allResponse.data;
        } else if (allResponse.data.data) {
          allData = Array.isArray(allResponse.data.data)
            ? allResponse.data.data
            : [allResponse.data.data];
        } else if (allResponse.data.rows) {
          allData = allResponse.data.rows;
        }

        if (allResponse.data.pagination) {
          totalCount = allResponse.data.pagination.total || allData.length;
        } else {
          totalCount = allData.length;
        }

        activeCount = allData.filter((t) => t.status === "active").length;
        onLeaveCount = allData.filter((t) => t.status === "on_leave").length;
        seniorCount = allData.filter(
          (t) =>
            t.teacher_status === "Senior Teacher" ||
            t.teacher_status === "School Leader",
        ).length;
        maleCount = allData.filter((t) => t.gender === "male").length;
        femaleCount = allData.filter((t) => t.gender === "female").length;
      }

      setAllTeachers(allData);

      setStats({
        total: totalCount,
        active: activeCount,
        onLeave: onLeaveCount,
        senior: seniorCount,
        male: maleCount,
        female: femaleCount,
      });

      const params = {
        page: 1,
        limit: 1000,
      };

      if (filterStatus !== "all") params.status = filterStatus;
      if (filterTeacherStatus !== "all")
        params.teacher_status = filterTeacherStatus;
      if (filterGender !== "all") params.gender = filterGender;

      const response = await apiClient.get("/teachers", { params });

      let data = [];

      if (response.data) {
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data.data) {
          data = Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data];
        } else if (response.data.rows) {
          data = response.data.rows;
        }
      }

      setTeachers(data);
      setTotalPages(response.data?.pagination?.pages || 1);
    } catch (error) {
      if (!options.background) {
        await showError({
          title: "Unable to Load Teachers",
          text:
            error.response?.data?.message ||
            "Failed to fetch teachers. Please try again.",
        });
      }
      console.error("Error fetching teachers:", error);
    } finally {
      if (!options.background) {
        setLoading(false);
      }
    }
  };

  // ─── Store ref ───
  useEffect(() => {
    fetchTeachersRef.current = fetchTeachers;
  }, [fetchTeachers]);

  // ─── Initial load ───
  useEffect(() => {
    fetchTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Socket listener – background updates ───
  useTeacherSocket((event) => {
    console.log("📡 Real‑time teacher update received:", event);
    if (fetchTeachersRef.current) {
      // Refresh silently – no loader or alert.
      fetchTeachersRef.current({ background: true });
    }
  });

  // ─── Effect for filter changes (re‑fetch) ───
  useEffect(() => {
    fetchTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterStatus, filterTeacherStatus, filterGender]);

  useEffect(() => {
    setSelectedTeachers([]);
  }, [filterStatus, filterTeacherStatus, filterGender, searchTerm]);

  // Remember the chosen layout and close the three-dot menu when clicking away.
  useEffect(() => {
    try {
      localStorage.setItem("teachersViewMode", viewMode);
    } catch {
      // Continue normally if browser storage is unavailable.
    }
  }, [viewMode]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setShowMenu(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setShowMenu(null);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchTeachers();
      return;
    }

    try {
      setLoading(true);
      const params = {
        search: searchTerm,
        limit: 1000,
      };

      if (filterStatus !== "all") params.status = filterStatus;
      if (filterTeacherStatus !== "all")
        params.teacher_status = filterTeacherStatus;
      if (filterGender !== "all") params.gender = filterGender;

      const response = await apiClient.get("/teachers", { params });

      let data = [];

      if (response.data) {
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data.data) {
          data = response.data.data;
        } else if (response.data.rows) {
          data = response.data.rows;
        }
      }

      setTeachers(data);
      setTotalPages(response.data?.pagination?.pages || 1);
    } catch (error) {
      await showError({
        title: "Search Failed",
        text:
          error.response?.data?.message ||
          "Unable to search teachers. Please try again.",
      });
      console.error("Error searching teachers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (teacher) => {
    if (!teacher || isDeleting) return;

    const teacherName =
      `${teacher.first_name || ""} ${teacher.last_name || ""}`.trim() ||
      "this teacher";

    const confirmed = await showConfirm({
      title: "Delete Teacher",
      text: `Are you sure you want to delete ${teacherName}? This will permanently remove the teacher and their data from this school. This action cannot be undone.`,
      confirmText: "Yes, delete",
      icon: "error",
    });

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await apiClient.delete(`/teachers/${teacher.id}`);
      setSelectedTeachers((current) =>
        current.filter((id) => id !== teacher.id),
      );
      await showSuccess({
        text: `${teacherName} was deleted successfully.`,
      });
      await fetchTeachers({ background: true });
    } catch (error) {
      console.error("Error deleting teacher:", error);
      await showError({
        text:
          error.response?.data?.message ||
          `Failed to delete ${teacherName}. Please try again.`,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (isDeleting) return;

    if (selectedTeachers.length === 0) {
      await showWarning({
        text: "Select at least one teacher before using bulk delete.",
      });
      return;
    }

    const deleteCount = selectedTeachers.length;
    const confirmed = await showConfirm({
      title: "Delete Selected Teachers",
      text: `You are about to permanently delete ${deleteCount} ${deleteCount === 1 ? "teacher" : "teachers"} and their data from this school. This action cannot be undone.`,
      confirmText: "Yes, delete",
      icon: "error",
    });

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await Promise.all(
        selectedTeachers.map((id) => apiClient.delete(`/teachers/${id}`)),
      );
      setSelectedTeachers([]);
      await showSuccess({
        text: `${deleteCount} ${deleteCount === 1 ? "teacher was" : "teachers were"} deleted successfully.`,
      });
      await fetchTeachers({ background: true });
    } catch (error) {
      console.error("Error deleting teachers:", error);
      await showError({
        text:
          error.response?.data?.message ||
          "Some or all selected teachers could not be deleted. Refresh the page and try again.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredTeachers = teachers.filter((teacher) => {
    const searchMatch =
      (teacher.first_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (teacher.last_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (teacher.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (teacher.employee_number || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const statusMatch =
      filterStatus === "all" || teacher.status === filterStatus;
    const teacherStatusMatch =
      filterTeacherStatus === "all" ||
      teacher.teacher_status === filterTeacherStatus;
    const genderMatch =
      filterGender === "all" || teacher.gender === filterGender;

    return searchMatch && statusMatch && teacherStatusMatch && genderMatch;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTeachers = filteredTeachers.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalFilteredPages = Math.ceil(filteredTeachers.length / itemsPerPage);

  const handleSelectAll = () => {
    if (
      selectedTeachers.length === filteredTeachers.length &&
      filteredTeachers.length > 0
    ) {
      setSelectedTeachers([]);
    } else {
      setSelectedTeachers(filteredTeachers.map((teacher) => teacher.id));
    }
  };

  const handleSelectTeacher = (id) => {
    if (selectedTeachers.includes(id)) {
      setSelectedTeachers(selectedTeachers.filter((sid) => sid !== id));
    } else {
      setSelectedTeachers([...selectedTeachers, id]);
    }
  };

  const getTeachersToPrint = () => {
    if (selectedTeachers.length > 0) {
      return filteredTeachers.filter((t) => selectedTeachers.includes(t.id));
    }
    return filteredTeachers;
  };

  const generateTeachersPrintHTML = () => {
    const printTeachers = getTeachersToPrint();
    const sortedTeachers = [...printTeachers].sort((a, b) =>
      (a.first_name || "").localeCompare(b.first_name || ""),
    );

    const filterInfo = [];
    if (filterStatus !== "all")
      filterInfo.push(`Status: ${filterStatus.toUpperCase()}`);
    if (filterTeacherStatus !== "all")
      filterInfo.push(`Role: ${filterTeacherStatus}`);
    if (filterGender !== "all")
      filterInfo.push(`Gender: ${filterGender.toUpperCase()}`);
    if (searchTerm) filterInfo.push(`Search: "${searchTerm}"`);
    const filterText =
      filterInfo.length > 0 ? ` (Filtered: ${filterInfo.join(", ")})` : "";

    const totalPrint = sortedTeachers.length;

    let tableRows = "";
    sortedTeachers.forEach((teacher, index) => {
      const fullName =
        `${teacher.first_name || ""} ${teacher.last_name || ""}`.trim() ||
        "N/A";
      const initials = getInitials(teacher.first_name, teacher.last_name);

      tableRows += `
        <tr>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${index + 1}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; font-size: 11px;">
            <div style="display:flex;align-items:center;gap:10px;">
              <div style="width:30px;height:30px;border-radius:50%;background:#1a3a5c;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:10px;flex-shrink:0;overflow:hidden;">
                ${
                  teacher.profile_picture
                    ? `<img src="${teacher.profile_picture}" style="width:100%;height:100%;object-fit:cover;" />`
                    : initials
                }
              </div>
              <span>${fullName}</span>
            </div>
          </td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${teacher.employee_number || "N/A"}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${teacher.gender === "male" ? "Male" : teacher.gender === "female" ? "Female" : "Other"}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${teacher.qualification || "N/A"}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${teacher.teacher_status || "N/A"}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">
            <span style="display:inline-block;padding:2px 10px;border-radius:3px;font-size:10px;font-weight:600;${teacher.status === "active" ? "background:#d4edda;color:#155724;" : teacher.status === "on_leave" ? "background:#fff3cd;color:#856404;" : "background:#f8d7da;color:#721c24;"}">
              ${getStatusLabel(teacher.status)}
            </span>
          </td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${teacher.years_of_experience || 0} years</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">
            ${teacher.created_at ? new Date(teacher.created_at).toLocaleDateString() : "N/A"}
          </td>
        </tr>
      `;
    });

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Teachers List - PawaSchool</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Times New Roman', Times, serif;
              padding: 25px 35px;
              color: #1a1a1a;
              background: white;
              font-size: 11px;
            }
            @page {
              size: A4 landscape;
              margin: 10mm 12mm;
            }
            @media print {
              .no-print { display: none !important; }
              body { padding: 15px 20px; }
              th { background: #1a3a5c !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .status-active { background: #d4edda !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .status-on_leave { background: #fff3cd !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
            .print-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 3px solid #1a3a5c;
              padding-bottom: 12px;
              margin-bottom: 15px;
            }
            .print-header-left h1 {
              font-size: 20px;
              color: #1a3a5c;
              font-weight: bold;
              letter-spacing: 1px;
              margin: 0;
            }
            .print-header-left p {
              color: #555;
              font-size: 11px;
              margin-top: 3px;
            }
            .print-header-right {
              text-align: right;
              font-size: 10px;
              color: #666;
            }
            .print-header-right p { margin: 2px 0; }
            .print-title {
              text-align: center;
              font-size: 14px;
              font-weight: bold;
              color: #1a3a5c;
              margin-bottom: 12px;
              letter-spacing: 0.5px;
            }
            .student-count-badge {
              display: inline-block;
              background: #1a3a5c;
              color: white;
              padding: 1px 10px;
              border-radius: 12px;
              font-size: 10px;
              font-weight: 600;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 5px;
            }
            th {
              background: #1a3a5c;
              color: white;
              padding: 7px 10px;
              text-align: left;
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              font-weight: 600;
            }
            th:first-child { text-align: center; }
            td {
              padding: 6px 10px;
              border-bottom: 1px solid #e8e8e8;
              font-size: 11px;
              vertical-align: middle;
            }
            tr:nth-child(even) { background: #fafafa; }
            .print-footer {
              text-align: center;
              margin-top: 18px;
              padding-top: 10px;
              border-top: 1px solid #e0e0e0;
              color: #888;
              font-size: 9px;
            }
            .print-footer p { margin-top: 2px; }
            .print-watermark {
              position: fixed;
              bottom: 30px;
              right: 30px;
              opacity: 0.06;
              font-size: 45px;
              color: #1a3a5c;
              font-weight: bold;
              transform: rotate(-20deg);
              pointer-events: none;
            }
            .page-number {
              text-align: center;
              font-size: 9px;
              color: #999;
              margin-top: 8px;
            }
            .record-count {
              text-align: right;
              font-size: 10px;
              color: #888;
              margin-top: 8px;
            }
          </style>
        </head>
        <body>
          <div class="print-watermark">PAWA SCHOOL</div>

          <div class="print-header">
            <div class="print-header-left">
              <h1>PAWASCHOOL</h1>
              <p>Teacher Management System - Complete Teachers List</p>
            </div>
            <div class="print-header-right">
              <p><strong>Generated:</strong> ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
              <p><strong>Time:</strong> ${new Date().toLocaleTimeString()}</p>
              <p><strong>Report ID:</strong> #${Math.random().toString(36).substring(2, 8).toUpperCase()}</p>
            </div>
          </div>

          <div class="print-title">
            TEACHERS LIST ${filterText}
            <span class="student-count-badge" style="margin-left:10px;">${totalPrint} Teachers</span>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width:4%;text-align:center;">#</th>
                <th style="width:18%;">Teacher Name</th>
                <th style="width:12%;text-align:center;">Employee #</th>
                <th style="width:8%;text-align:center;">Gender</th>
                <th style="width:14%;">Qualification</th>
                <th style="width:12%;">Role</th>
                <th style="width:10%;text-align:center;">Status</th>
                <th style="width:10%;text-align:center;">Experience</th>
                <th style="width:12%;text-align:center;">Joined</th>
              </tr>
            </thead>
            <tbody>
              ${
                tableRows ||
                `
                <tr>
                  <td colspan="9" style="text-align:center;padding:30px;color:#999;font-size:14px;">
                    No teachers found matching the current filters.
                  </td>
                </tr>
              `
              }
            </tbody>
          </table>

          <div class="record-count">
            Total Records: <strong>${totalPrint}</strong>
          </div>

          <div class="print-footer">
            <p>This is a computer-generated document. No signature required.</p>
            <p>${new Date().getFullYear()} PawaSchool Management System - All Rights Reserved</p>
            <p style="font-size:8px;color:#bbb;margin-top:3px;">Confidential - For authorized personnel only</p>
          </div>

          <div class="page-number">Page 1 of 1</div>
        </body>
      </html>
    `;
  };

  const handlePrint = () => {
    if (isStarterPlan) {
      return showWarning({
        title: "PDF Download Not Available",
        text: "PDF downloads are not included in the Starter plan. Upgrade to Basic or a higher plan to download PDFs.",
      });
    }

    const teachersToPrint = getTeachersToPrint();
    if (teachersToPrint.length === 0) {
      showWarning({
        title: "No Teachers to Print",
        text: "There are no teachers matching the current filters.",
      });
      return;
    }

    setIsPrinting(true);
    setShowPrintMenu(false);

    try {
      const printWindow = window.open("", "_blank", "width=1100,height=800");
      if (printWindow) {
        printWindow.document.write(generateTeachersPrintHTML());
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    } catch (error) {
      console.error("Print error:", error);
      showError({
        title: "Print Failed",
        text: "Failed to generate the teachers print preview.",
      });
    } finally {
      setIsPrinting(false);
    }
  };

  const handleExportWord = () => {
    const teachersToExport = getTeachersToPrint();
    if (teachersToExport.length === 0) {
      showWarning({
        title: "No Teachers to Export",
        text: "There are no teachers matching the current filters.",
      });
      return;
    }

    setIsExportingWord(true);
    setShowPrintMenu(false);

    try {
      const wordHTML = generateTeachersPrintHTML();

      if (!wordHTML) {
        showWarning({
          title: "No Data to Export",
          text: "No teacher information is available for export.",
        });
        return;
      }

      const wordDoc = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:w="urn:schemas-microsoft-com:office:word"
              xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8">
          <title>Teachers List</title>
          <!--[if gte mso 9]>
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
            </w:WordDocument>
          </xml>
          <![endif]-->
          ${wordHTML.match(/<style>([\s\S]*?)<\/style>/)?.[0] || ""}
        </head>
        <body>
          ${wordHTML.match(/<body>([\s\S]*?)<\/body>/)?.[1] || ""}
        </body>
        </html>
      `;

      const blob = new Blob([wordDoc], {
        type: "application/msword;charset=utf-8",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filterSuffix = [];
      if (filterStatus !== "all") filterSuffix.push(filterStatus);
      if (filterTeacherStatus !== "all")
        filterSuffix.push(filterTeacherStatus.replace(/\s/g, "_"));
      if (filterGender !== "all") filterSuffix.push(filterGender);
      const fileName = `Teachers_List${filterSuffix.length > 0 ? "_" + filterSuffix.join("_") : ""}_${new Date().toISOString().split("T")[0]}.doc`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess({
        title: "Export Complete",
        text: "The teachers Word document was downloaded successfully.",
      });
    } catch (error) {
      console.error("Export Word error:", error);
      showError({
        title: "Export Failed",
        text: "Failed to export the teachers Word document.",
      });
    } finally {
      setIsExportingWord(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "bg-green-100 text-green-700",
      inactive: "bg-gray-100 text-gray-700",
      on_leave: "bg-yellow-100 text-yellow-700",
      terminated: "bg-red-100 text-red-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: "Active",
      inactive: "Inactive",
      on_leave: "On Leave",
      terminated: "Terminated",
    };
    return labels[status] || status;
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterTeacherStatus("all");
    setFilterGender("all");
    setSelectedTeachers([]);
    setCurrentPage(1);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filterStatus !== "all") count++;
    if (filterTeacherStatus !== "all") count++;
    if (filterGender !== "all") count++;
    if (searchTerm) count++;
    return count;
  };

  const handleViewTeacher = (id) => {
    navigate(`/app/teachers/${id}`);
  };

  const changeViewMode = (mode) => {
    setViewMode(mode);
    setShowMenu(null);
  };

  const renderTeacherActions = (teacher) => (
    <div
      className="relative"
      ref={showMenu === teacher.id ? actionMenuRef : null}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        onClick={() =>
          setShowMenu((current) => (current === teacher.id ? null : teacher.id))
        }
        className={`flex h-9 w-9 items-center justify-center rounded-xl border transition-all ${
          showMenu === teacher.id
            ? "border-primary-200 bg-primary-50 text-primary-700"
            : "border-transparent text-gray-400 hover:border-gray-200 hover:bg-gray-50 hover:text-gray-700"
        }`}
        aria-label={`Open actions for ${teacher.first_name || ""} ${teacher.last_name || ""}`}
        aria-expanded={showMenu === teacher.id}
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>

      {showMenu === teacher.id && (
        <div className="absolute right-0 top-11 z-40 w-48 overflow-hidden rounded-xl border border-gray-100 bg-white py-1.5 shadow-xl shadow-gray-200/70">
          <button
            type="button"
            onClick={() => {
              setShowMenu(null);
              navigate(`/app/teachers/${teacher.id}`);
            }}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <EyeIcon className="h-4 w-4 text-gray-400" />
            View teacher
          </button>
          <button
            type="button"
            onClick={() => {
              setShowMenu(null);
              navigate(`/app/teachers/edit/${teacher.id}`);
            }}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <PencilIcon className="h-4 w-4 text-gray-400" />
            Edit teacher
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button
            type="button"
            onClick={() => {
              setShowMenu(null);
              handleDelete(teacher);
            }}
            disabled={isDeleting}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <TrashIcon className="h-4 w-4" />
            Delete teacher
          </button>
        </div>
      )}
    </div>
  );

  // Gender Pie Chart Component – flattened
  const GenderPieChart = () => {
    const total = stats.male + stats.female;
    const malePercentage =
      total > 0 ? Math.round((stats.male / total) * 100) : 0;
    const femalePercentage =
      total > 0 ? Math.round((stats.female / total) * 100) : 0;

    const radius = 80;
    const circumference = 2 * Math.PI * radius;
    const maleArc = (malePercentage / 100) * circumference;
    const femaleArc = (femalePercentage / 100) * circumference;

    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-primary-700 mb-4 sm:mb-6 text-center">
          Gender Distribution
        </h3>
        <div className="flex flex-col lg:flex-row items-center justify-center gap-6 sm:gap-8">
          <div className="relative w-48 h-48 sm:w-64 sm:h-64">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 256 256"
              className="transform -rotate-90"
            >
              <circle
                cx="128"
                cy="128"
                r={radius}
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="32"
              />

              {stats.male > 0 && (
                <circle
                  cx="128"
                  cy="128"
                  r={radius}
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="32"
                  strokeDasharray={`${maleArc} ${circumference}`}
                  strokeDashoffset={0}
                  strokeLinecap="round"
                  className="transition-all duration-1000"
                  onMouseEnter={() => setHoveredGender("male")}
                  onMouseLeave={() => setHoveredGender(null)}
                  style={{ cursor: "pointer" }}
                />
              )}

              {stats.female > 0 && (
                <circle
                  cx="128"
                  cy="128"
                  r={radius}
                  fill="none"
                  stroke="#ec4899"
                  strokeWidth="32"
                  strokeDasharray={`${femaleArc} ${circumference}`}
                  strokeDashoffset={-maleArc}
                  strokeLinecap="round"
                  className="transition-all duration-1000"
                  onMouseEnter={() => setHoveredGender("female")}
                  onMouseLeave={() => setHoveredGender(null)}
                  style={{ cursor: "pointer" }}
                />
              )}
            </svg>

            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-2xl sm:text-3xl font-bold text-primary-700">
                {total}
              </div>
              <div className="text-xs sm:text-sm text-primary-400">
                Total Teachers
              </div>
            </div>

            {hoveredGender && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 z-10 pointer-events-none">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor:
                        hoveredGender === "male" ? "#3b82f6" : "#ec4899",
                    }}
                  />
                  <div>
                    <p className="font-semibold text-primary-700 capitalize text-sm sm:text-base">
                      {hoveredGender}
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-primary-600">
                      {hoveredGender === "male" ? stats.male : stats.female}
                      <span className="text-xs sm:text-sm font-normal text-primary-400 ml-1">
                        (
                        {hoveredGender === "male"
                          ? malePercentage
                          : femalePercentage}
                        %)
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:gap-4 min-w-[160px] sm:min-w-[200px] w-full sm:w-auto">
            <div className="flex items-center justify-between p-2 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-blue-500"></div>
                <span className="text-sm font-medium text-primary-700">
                  Male
                </span>
              </div>
              <div className="text-right">
                <span className="text-base sm:text-lg font-bold text-blue-600">
                  {stats.male}
                </span>
                <span className="text-xs sm:text-sm text-primary-400 ml-1 sm:ml-2">
                  ({malePercentage}%)
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between p-2 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-pink-500"></div>
                <span className="text-sm font-medium text-primary-700">
                  Female
                </span>
              </div>
              <div className="text-right">
                <span className="text-base sm:text-lg font-bold text-pink-600">
                  {stats.female}
                </span>
                <span className="text-xs sm:text-sm text-primary-400 ml-1 sm:ml-2">
                  ({femalePercentage}%)
                </span>
              </div>
            </div>

            <div className="mt-1 sm:mt-2 pt-2 sm:pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between p-1 sm:p-2">
                <span className="text-sm font-medium text-primary-400">
                  Total
                </span>
                <span className="text-base sm:text-lg font-bold text-primary-700">
                  {total}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <FireLoader fullScreen={true} size="lg" text="LOADING..." />;
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-4 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-primary-700">
            Teachers
          </h1>
          <p className="text-sm sm:text-base text-primary-400 mt-0.5 sm:mt-1">
            Manage all teachers in the system
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {selectedTeachers.length > 0 && (
            <>
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleBulkDelete();
                }}
                disabled={isDeleting}
                className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isDeleting ? (
                  <span className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  <TrashIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                )}
                <span className="hidden xs:inline">
                  {isDeleting ? "Deleting..." : "Delete Selected"}
                </span>
                <span className="xs:hidden">
                  {isDeleting ? "Deleting..." : "Delete"}
                </span>
                <span className="ml-1">({selectedTeachers.length})</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowPrintMenu(!showPrintMenu)}
                  className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  <PrinterIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                  <span className="hidden xs:inline">Print / Export</span>
                  <span className="xs:hidden">Export</span>
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {showPrintMenu && (
                  <div className="absolute right-0 mt-2 w-48 sm:w-64 bg-white rounded-lg shadow-md border border-gray-200 py-1 z-50">
                    <button
                      onClick={handlePrint}
                      disabled={isPrinting}
                      className="w-full flex items-center px-3 sm:px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <DocumentIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-blue-600" />
                      <span>
                        {isPrinting ? "Preparing..." : "Print as PDF"}
                      </span>
                      <span className="ml-auto text-xs text-gray-400 hidden sm:inline">
                        Ctrl+P
                      </span>
                    </button>
                    <button
                      onClick={handleExportWord}
                      disabled={isExportingWord}
                      className="w-full flex items-center px-3 sm:px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 border-t border-gray-100"
                    >
                      <DocumentArrowDownIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-blue-600" />
                      <span>
                        {isExportingWord ? "Exporting..." : "Export as Word"}
                      </span>
                      <span className="ml-auto text-xs text-gray-400 hidden sm:inline">
                        .doc
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          <button
            onClick={() => navigate("/app/teachers/add")}
            className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
          >
            <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
            <span>Add Teacher</span>
          </button>
        </div>
      </div>

      {/* Stats Cards – flattened */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-lg p-3 sm:p-5 border border-gray-100 hover:border-primary-200 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-primary-400">
                Total Teachers
              </p>
              <p className="text-xl sm:text-3xl font-bold text-primary-700 mt-0.5">
                {stats.total}
              </p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
              <AcademicCapIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-gray-50">
            <p className="text-[10px] sm:text-xs text-primary-400">
              All registered teachers
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-3 sm:p-5 border border-gray-100 hover:border-green-200 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-primary-400">
                Active Teachers
              </p>
              <p className="text-xl sm:text-3xl font-bold text-green-600 mt-0.5">
                {stats.active}
              </p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
              <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-gray-50">
            <p className="text-[10px] sm:text-xs text-primary-400">
              {stats.total > 0
                ? Math.round((stats.active / stats.total) * 100)
                : 0}
              % of total
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-3 sm:p-5 border border-gray-100 hover:border-amber-200 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-primary-400">
                On Leave
              </p>
              <p className="text-xl sm:text-3xl font-bold text-amber-600 mt-0.5">
                {stats.onLeave}
              </p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center">
              <XCircleIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-gray-50">
            <p className="text-[10px] sm:text-xs text-primary-400">
              Currently on leave
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-3 sm:p-5 border border-gray-100 hover:border-blue-200 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-primary-400">
                Senior Teachers
              </p>
              <p className="text-xl sm:text-3xl font-bold text-blue-600 mt-0.5">
                {stats.senior}
              </p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              <UserGroupIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-gray-50">
            <p className="text-[10px] sm:text-xs text-primary-400">
              Senior + School Leaders
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filter – flattened */}
      <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-3 sm:gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-primary-400" />
            <input
              type="text"
              placeholder="Search teachers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 placeholder-primary-400 text-sm transition-colors"
            />
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="flex-1 min-w-[100px] px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm transition-colors"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="on_leave">On Leave</option>
              <option value="terminated">Terminated</option>
            </select>
            <select
              value={filterTeacherStatus}
              onChange={(e) => setFilterTeacherStatus(e.target.value)}
              className="flex-1 min-w-[100px] px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm transition-colors"
            >
              <option value="all">All Roles</option>
              <option value="School Leader">School Leader</option>
              <option value="Deputy">Deputy</option>
              <option value="Senior Teacher">Senior Teacher</option>
              <option value="Teacher">Teacher</option>
            </select>
            <select
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value)}
              className="flex-1 min-w-[100px] px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm transition-colors"
            >
              <option value="all">All Genders</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            <button
              onClick={handleSearch}
              className="flex-1 px-4 py-2 sm:px-4 sm:py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
            >
              Search
            </button>
            {getActiveFilterCount() > 0 && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 sm:px-4 sm:py-2.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1.5 sm:gap-2 text-sm"
              >
                <XCircleIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden xs:inline">Clear Filters</span>
                <span className="xs:hidden">Clear</span>
                <span className="text-xs">({getActiveFilterCount()})</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Teachers Grid with Select All Header – flattened */}
      <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
        {/* Select All Header */}
        <div className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-50/50 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3">
            <input
              type="checkbox"
              checked={
                selectedTeachers.length === filteredTeachers.length &&
                filteredTeachers.length > 0
              }
              onChange={handleSelectAll}
              className="w-4 h-4 text-primary-500 rounded border-gray-300 focus:ring-primary-200"
            />
            <span className="text-xs sm:text-sm font-medium text-primary-700">
              {selectedTeachers.length === filteredTeachers.length &&
              filteredTeachers.length > 0
                ? "Deselect All"
                : "Select All"}
            </span>
            <span className="text-[10px] sm:text-xs text-primary-400">
              ({filteredTeachers.length} teacher
              {filteredTeachers.length !== 1 ? "s" : ""})
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-primary-500 sm:inline">
              {selectedTeachers.length} selected
            </span>
            <div
              className="inline-flex items-center rounded-xl border border-gray-200 bg-white p-1 shadow-sm"
              aria-label="Choose teachers layout"
            >
              <button
                type="button"
                onClick={() => changeViewMode("grid")}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${
                  viewMode === "grid"
                    ? "bg-primary-600 text-white shadow-sm"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                }`}
                title="Card view"
              >
                <Squares2X2Icon className="h-4 w-4" />
                <span className="hidden md:inline">Cards</span>
              </button>
              <button
                type="button"
                onClick={() => changeViewMode("list")}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${
                  viewMode === "list"
                    ? "bg-primary-600 text-white shadow-sm"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                }`}
                title="List view"
              >
                <ListBulletIcon className="h-4 w-4" />
                <span className="hidden md:inline">List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Teachers Card / List Layout */}
        {currentTeachers.length === 0 ? (
          <div className="p-4 sm:p-6">
            <div className="col-span-full text-center py-8 sm:py-12">
              <UserGroupIcon className="mx-auto text-primary-200 w-12 h-12 sm:w-16 sm:h-16" />
              <h3 className="text-base sm:text-lg font-medium text-primary-700 mt-3 sm:mt-4">
                No teachers found
              </h3>
              <p className="text-sm text-primary-400">
                Start by adding your first teacher
              </p>
              <button
                onClick={() => navigate("/teachers/add")}
                className="mt-3 sm:mt-4 inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
              >
                <PlusIcon className="w-4 h-4 mr-1.5" />
                Add Teacher
              </button>
            </div>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2 sm:gap-5 sm:p-6 xl:grid-cols-3">
            {currentTeachers.map((teacher) => (
              <div
                key={teacher.id}
                onClick={() => handleViewTeacher(teacher.id)}
                className={`group relative cursor-pointer overflow-visible rounded-2xl border bg-white transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-lg hover:shadow-gray-200/60 ${
                  selectedTeachers.includes(teacher.id)
                    ? "border-primary-300 ring-2 ring-primary-100"
                    : "border-gray-200"
                }`}
              >
                <div className="h-1.5 rounded-t-2xl bg-gradient-to-r from-primary-500 to-primary-300" />
                <div className="p-4 sm:p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 sm:gap-4 cursor-pointer group flex-1 min-w-0">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden ring-1 ring-gray-100 group-hover:ring-2 group-hover:ring-primary-300 transition-all">
                        {teacher.profile_picture ? (
                          <img
                            src={teacher.profile_picture}
                            alt={`${teacher.first_name} ${teacher.last_name}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-primary-600 font-bold text-lg sm:text-xl">
                            {getInitials(teacher.first_name, teacher.last_name)}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-primary-700 group-hover:text-primary-600 transition-colors truncate text-sm sm:text-base">
                          {teacher.first_name} {teacher.last_name}
                        </h3>
                        <p className="text-xs sm:text-sm text-primary-400">
                          #{teacher.employee_number}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
                      <input
                        type="checkbox"
                        checked={selectedTeachers.includes(teacher.id)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => handleSelectTeacher(teacher.id)}
                        className="w-4 h-4 text-primary-500 rounded border-gray-300 focus:ring-primary-200"
                      />
                      {renderTeacherActions(teacher)}
                    </div>
                  </div>

                  <div className="mt-3 sm:mt-4 space-y-1.5 sm:space-y-2">
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-primary-600">
                      <UserIcon className="w-3 h-3 sm:w-4 sm:h-4 text-primary-400" />
                      <span className="font-medium">Gender:</span>
                      <span className="capitalize">
                        {teacher.gender || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-primary-600">
                      <AcademicCapIcon className="w-3 h-3 sm:w-4 sm:h-4 text-primary-400" />
                      <span className="font-medium">Qualification:</span>
                      <span>{teacher.qualification || "N/A"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-primary-600">
                      <BookOpenIcon className="w-3 h-3 sm:w-4 sm:h-4 text-primary-400" />
                      <span className="font-medium">Role:</span>
                      <span>{teacher.teacher_status || "N/A"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-primary-600">
                      <EnvelopeIcon className="w-3 h-3 sm:w-4 sm:h-4 text-primary-400" />
                      <span className="font-medium">Class Taught:</span>
                      <span>{teacher.class_taught || "N/A"}</span>
                    </div>
                  </div>

                  <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
                    <span
                      className={`px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full ${getStatusColor(teacher.status)}`}
                    >
                      {getStatusLabel(teacher.status)}
                    </span>
                    <span className="text-xs sm:text-sm text-primary-400">
                      {teacher.years_of_experience || 0} years exp.
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3 p-4 sm:p-6">
            {currentTeachers.map((teacher) => (
              <div
                key={teacher.id}
                onClick={() => handleViewTeacher(teacher.id)}
                className={`group cursor-pointer rounded-2xl border bg-white p-4 transition-all duration-200 hover:border-primary-200 hover:shadow-md hover:shadow-gray-200/50 sm:p-5 ${
                  selectedTeachers.includes(teacher.id)
                    ? "border-primary-300 ring-2 ring-primary-100"
                    : "border-gray-200"
                }`}
              >
                <div className="flex items-start gap-3 sm:items-center sm:gap-4">
                  <input
                    type="checkbox"
                    checked={selectedTeachers.includes(teacher.id)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => handleSelectTeacher(teacher.id)}
                    className="mt-3 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary-500 focus:ring-primary-200 sm:mt-0"
                  />

                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-100 ring-1 ring-gray-100 transition-all group-hover:ring-2 group-hover:ring-primary-300 sm:h-12 sm:w-12">
                    {teacher.profile_picture ? (
                      <img
                        src={teacher.profile_picture}
                        alt={`${teacher.first_name} ${teacher.last_name}`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="font-bold text-primary-600">
                        {getInitials(teacher.first_name, teacher.last_name)}
                      </span>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-sm font-semibold text-primary-700 transition-colors group-hover:text-primary-600 sm:text-base">
                      {teacher.first_name} {teacher.last_name}
                    </h3>
                    <p className="mt-0.5 text-xs text-primary-400">
                      #{teacher.employee_number || "N/A"}
                    </p>
                  </div>

                  <div className="hidden min-w-[150px] max-w-[200px] lg:block">
                    <p className="text-xs text-gray-400">Role</p>
                    <p className="mt-0.5 truncate text-sm font-medium text-gray-700">
                      {teacher.teacher_status || "N/A"}
                    </p>
                  </div>

                  <div className="hidden min-w-[170px] max-w-[230px] xl:block">
                    <p className="text-xs text-gray-400">Contact</p>
                    <p className="mt-0.5 truncate text-sm text-gray-700">
                      {teacher.phone || "Not provided"}
                    </p>
                  </div>

                  <div className="hidden min-w-[100px] md:block">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(teacher.status)}`}
                    >
                      {getStatusLabel(teacher.status)}
                    </span>
                  </div>

                  <div className="hidden min-w-[85px] text-right sm:block">
                    <p className="text-xs text-gray-400">Experience</p>
                    <p className="mt-0.5 text-sm font-medium text-gray-700">
                      {teacher.years_of_experience || 0} years
                    </p>
                  </div>

                  {renderTeacherActions(teacher)}
                </div>

                <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3 sm:hidden">
                  <span className="truncate text-xs text-gray-500">
                    {teacher.teacher_status || "Teacher"}
                  </span>
                  <span className="text-gray-300">•</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${getStatusColor(teacher.status)}`}
                  >
                    {getStatusLabel(teacher.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination – flattened */}
        {totalFilteredPages > 1 && (
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <p className="text-xs sm:text-sm text-primary-400 text-center sm:text-left">
              Showing {indexOfFirstItem + 1} to{" "}
              {Math.min(indexOfLastItem, filteredTeachers.length)} of{" "}
              {filteredTeachers.length} teachers
            </p>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-1.5 sm:p-2 rounded-lg border border-gray-200 text-primary-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              {Array.from(
                { length: Math.min(totalFilteredPages, 5) },
                (_, i) => {
                  let pageNum;
                  if (totalFilteredPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalFilteredPages - 2) {
                    pageNum = totalFilteredPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-2.5 py-1 sm:px-4 sm:py-2 rounded-lg transition-colors text-sm ${
                        currentPage === pageNum
                          ? "bg-primary-600 text-white"
                          : "text-primary-600 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                },
              )}
              <button
                onClick={() =>
                  setCurrentPage((prev) =>
                    Math.min(prev + 1, totalFilteredPages),
                  )
                }
                disabled={currentPage === totalFilteredPages}
                className="p-1.5 sm:p-2 rounded-lg border border-gray-200 text-primary-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Gender Distribution Pie Chart */}
      {(stats.male > 0 || stats.female > 0) && <GenderPieChart />}
    </div>
  );
}